import React, { createContext, useContext, useState } from 'react';
import { Platform } from 'react-native'; // Mission 20: Platform detection
import type { ReactNode } from 'react';
import type { NavigationState } from '../types';
import { storage } from '../services/storage'; // Mission 20: Unified storage

interface ChatContext {
  context: string;
  initialMessage: string;
}

export type NavigationSource = 
  | 'today'           // From MyPath today view
  | 'browse-goals'    // From browse goals
  | 'browse-plans'    // From browse plans
  | 'browse-sessions' // From browse sessions
  | 'plan-detail'     // From plan detail page
  | 'goal-detail'     // From goal detail page
  | 'workout-detail'  // From workout detail page
  | 'chat'            // From chat (deep link)
  | 'unknown';        // Fallback

interface NavigationHistoryItem {
  view: 'list' | 'detail' | 'peekModal';
  selectedItem?: {
    type: 'goal' | 'plan' | 'workout' | 'mobility' | 'restDay';
    id: string;
  };
  source?: NavigationSource; // Track where navigation came from
  timestamp: number;
}

interface NavigationContextType {
  navigationState: NavigationState;
  navigateToDetail: (type: 'goal' | 'plan' | 'workout' | 'mobility' | 'restDay', id: string, source?: NavigationSource) => void;
  showPeekModal: (type: 'goal' | 'plan' | 'workout' | 'mobility' | 'restDay', id: string) => void;
  closePeekModal: () => void;
  goBack: () => void;
  resetToList: () => void; // Complete reset to list view (for home button)
  navigateToChat: (chatContext: ChatContext) => Promise<void>;
  getCurrentSource: () => NavigationSource | undefined; // Get source for current navigation
}

const NavigationContext = createContext<NavigationContextType | undefined>(undefined);

export const useNavigation = () => {
  const context = useContext(NavigationContext);
  if (!context) {
    throw new Error('useNavigation must be used within a NavigationProvider');
  }
  return context;
};

/**
 * Non-throwing variant: the context, or `null` when no NavigationProvider is
 * mounted above the caller.
 *
 * The detail pages render both as stateful overlays (provider present) and as
 * plain routes (provider absent), and used to cope by wrapping `useNavigation()`
 * in a try/catch or an optional call. Both are Rules-of-Hooks violations — a
 * hook inside a try block, a hook behind `?.` — and both hide the real intent,
 * which is simply "the provider is optional here". This hook is called
 * unconditionally and returns null instead of throwing.
 */
export const useOptionalNavigation = (): NavigationContextType | null => {
  return useContext(NavigationContext) ?? null;
};

interface NavigationProviderProps {
  children: ReactNode;
}

export const NavigationProvider: React.FC<NavigationProviderProps> = (props) => {
  // 🔧 STRICTMODE FIX (Jan 21, 2026): Handle React reconciliation passes
  const { children } = props || {};
  
  
  const [navigationState, setNavigationState] = useState<NavigationState>({
    currentView: 'list'
  });
  
  const [navigationHistory, setNavigationHistory] = useState<NavigationHistoryItem[]>([
    { view: 'list', timestamp: Date.now() }
  ]);

  // Track current source for detail pages
  const [currentSource, setCurrentSource] = useState<NavigationSource | undefined>(undefined);

  // Skip render if no children (React reconciliation pass).
  //
  // RULES OF HOOKS: this guard MUST stay below every hook call. It used to
  // sit above the three useState calls, so the provider registered 0 hooks
  // on a childless pass and 3 otherwise — and `children` is a runtime-
  // variable prop, so a mounted instance really can swing between them and
  // throw "Rendered more hooks than during the previous render".
  if (!children) {
    return null;
  }

  const addToHistory = (newState: NavigationState, source?: NavigationSource) => {
    const historyItem: NavigationHistoryItem = {
      view: newState.currentView,
      selectedItem: newState.selectedItem,
      source: source || currentSource,
      timestamp: Date.now()
    };
    
    setNavigationHistory(prev => [...prev, historyItem]);
  };

  const navigateToDetail = (type: 'goal' | 'plan' | 'workout' | 'mobility' | 'restDay', id: string, source?: NavigationSource) => {
    console.log('🔄 NavigationContext: navigateToDetail called');
    console.log('🔄 NavigationContext: type =', type, 'id =', id?.substring(0, 8), 'source =', source);
    console.log('🔍 CURRENT STATE BEFORE NAVIGATION:', JSON.stringify({
      view: navigationState.currentView,
      itemType: navigationState.selectedItem?.type,
      itemId: navigationState.selectedItem?.id?.substring(0, 8),
      currentSource: currentSource
    }));
    
    // 🔧 FIX FEB 4, 2026: Add to history BEFORE updating currentSource
    // Bug: If you update currentSource first, then addToHistory uses WRONG source
    // Example: Plan (source='today') → Workout
    //   OLD: setCurrentSource('plan-detail'), then addToHistory with 'plan-detail'
    //   Result: Plan added with source 'plan-detail' (WRONG! Should be 'today')
    //   NEW: addToHistory with CURRENT source, then setCurrentSource('plan-detail')
    //   Result: Plan added with source 'today' ✅
    
    // Add current state to history FIRST (with existing currentSource)
    console.log('📚 PUSHING TO HISTORY (with current source):', JSON.stringify({
      view: navigationState.currentView,
      itemType: navigationState.selectedItem?.type,
      itemId: navigationState.selectedItem?.id?.substring(0, 8),
      source: currentSource  // ← Use EXISTING source (before update)
    }));
    addToHistory(navigationState, currentSource);
    
    // NOW update source for the NEW navigation
    const navigationSource = source || determineSourceFromState();
    console.log('🔄 NavigationContext: NOW setting currentSource to:', navigationSource);
    setCurrentSource(navigationSource);
    
    const newState: NavigationState = {
      currentView: 'detail',
      selectedItem: { type, id }
    };
    
    console.log('🔄 NavigationContext: Setting navigationState to:', JSON.stringify({
      view: newState.currentView,
      itemType: newState.selectedItem?.type,
      itemId: newState.selectedItem?.id?.substring(0, 8)
    }));
    console.log('🔍 HISTORY AFTER PUSH (length):', navigationHistory.length + 1);
    setNavigationState(newState);
  };

  // Helper to determine source from current navigation state
  const determineSourceFromState = (): NavigationSource => {
    if (navigationState.currentView === 'detail') {
      if (navigationState.selectedItem?.type === 'plan') return 'plan-detail';
      if (navigationState.selectedItem?.type === 'goal') return 'goal-detail';
    }
    // Check viewMode from MyPath (would need to be passed or stored)
    // For now, default to 'unknown' and let callers specify
    return 'unknown';
  };

  const showPeekModal = (type: 'goal' | 'plan' | 'workout' | 'mobility' | 'restDay', id: string) => {
    // Add current state to history before showing modal
    addToHistory(navigationState);
    
    setNavigationState(prev => ({
      ...prev,
      currentView: 'peekModal',
      peekModalItem: { type, id }
    }));
  };

  const closePeekModal = () => {
    console.log('🚨 NavigationContext: closePeekModal called');
    console.log('🚨 NavigationContext: Before - currentView =', navigationState.currentView);
    console.log('🚨 NavigationContext: Before - peekModalItem =', navigationState.peekModalItem);
    
    setNavigationState(prev => {
      const newView = prev.selectedItem ? 'detail' : 'list';
      console.log('🚨 NavigationContext: Setting currentView to:', newView);
      return {
        ...prev,
        currentView: newView,
        peekModalItem: undefined
      };
    });
    
    // Log after state update (will show in next render)
    setTimeout(() => {
      console.log('🚨 NavigationContext: After closePeekModal - currentView =', navigationState.currentView);
    }, 0);
  };

  const goBack = () => {
    console.log('⬅️ NavigationContext: goBack called');
    console.log('⬅️ NavigationContext: navigationHistory.length =', navigationHistory.length);
    console.log('⬅️ NavigationContext: Current currentSource =', currentSource);
    console.log('🔍 FULL HISTORY BEFORE POP:', JSON.stringify(navigationHistory.map((h, i) => ({
      index: i,
      view: h.view,
      itemType: h.selectedItem?.type,
      itemId: h.selectedItem?.id?.substring(0, 8),
      source: h.source
    })), null, 2));
    
    if (navigationHistory.length > 1) {
      // 🔧 FIX FEB 4, 2026: Restore to POPPED state, not previous!
      // 
      // THE BUG (OLD LOGIC):
      // History: [list0, list1, plan]
      // Back from workout:
      //   Pop plan → poppedState = plan
      //   previousState = list1 → Restore to list1
      //   Result: Goes to Today (wrong!)
      // 
      // THE FIX (NEW LOGIC):
      // History: [list0, list1, plan]
      // Back from workout:
      //   Pop plan → stateToRestoreTo = plan
      //   Restore TO plan ✅
      //   Result: Shows plan detail (correct!)
      // 
      // The history stores "where you were" before each navigation.
      // When you back, you restore TO the popped state (not to the one before it).
      
      const newHistory = [...navigationHistory];
      const stateToRestoreTo = newHistory.pop(); // This is where we should go back to
      
      console.log('⬅️ NavigationContext: POPPED STATE (restoring TO this) =', JSON.stringify({
        view: stateToRestoreTo?.view,
        itemType: stateToRestoreTo?.selectedItem?.type,
        itemId: stateToRestoreTo?.selectedItem?.id?.substring(0, 8),
        source: stateToRestoreTo?.source
      }));
      
      if (!stateToRestoreTo) {
        console.error('❌ NavigationContext: stateToRestoreTo is undefined! Cannot go back.');
        return;
      }
      
      console.log('⬅️ NavigationContext: Restoring source to:', stateToRestoreTo.source);
      
      // Restore to the popped state
      setCurrentSource(stateToRestoreTo.source);
      
      setNavigationHistory(newHistory);
      setNavigationState({
        currentView: stateToRestoreTo.view,
        selectedItem: stateToRestoreTo.selectedItem,
        peekModalItem: undefined
      });
      
      console.log('✅ NavigationContext: goBack complete - should now show:', 
        stateToRestoreTo.view === 'detail' ? `${stateToRestoreTo.selectedItem?.type} detail` : 'list view');
      
      // Log after state update attempt
      setTimeout(() => {
        console.log('⬅️ NavigationContext: After goBack (async check) - currentSource =', currentSource);
        console.log('⬅️ NavigationContext: After goBack (async check) - navigationState.currentView =', navigationState.currentView);
      }, 0);
    } else {
      console.log('⬅️ NavigationContext: No history - falling back to list view');
      // Fallback to list view if no history
      setCurrentSource(undefined);
      setNavigationState({
        currentView: 'list',
        selectedItem: undefined,
        peekModalItem: undefined
      });
    }
  };

  const resetToList = () => {
    console.log('🏠 NavigationContext: resetToList called - full reset to list view');
    console.log('🏠 NavigationContext: Before reset - currentView =', navigationState.currentView);
    console.log('🏠 NavigationContext: Before reset - history length =', navigationHistory.length);
    
    // Complete reset: clear history, clear current source, reset to list view
    setNavigationHistory([]);
    setCurrentSource(undefined);
    setNavigationState({
      currentView: 'list',
      selectedItem: undefined,
      peekModalItem: undefined
    });
    
    console.log('✅ NavigationContext: resetToList complete - now in clean list view');
  };

  const getCurrentSource = () => {
    console.log('🔍 NavigationContext: getCurrentSource called, returning:', currentSource);
    return currentSource;
  };

  const navigateToChat = async (chatContext: ChatContext) => {
    // Store chat context in storage for the Chat page to pick up
    await storage.setItem('chatContext', JSON.stringify(chatContext));
    
    // Navigate to chat page (web-only custom event)
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('navigateToChat', { 
        detail: chatContext 
      }));
    }
  };

  return (
    <NavigationContext.Provider
      value={{
        navigationState,
        navigateToDetail,
        showPeekModal,
        closePeekModal,
        goBack,
        resetToList,
        navigateToChat,
        getCurrentSource
      }}
    >
      {children}
    </NavigationContext.Provider>
  );
}; 